import type { BankName } from "@/api/custom_types";
import { ALL_BANKS } from "@/constants/banks";
import { useTranslation } from "@/hooks";
import type { TranslationKeys } from "@/hooks/useTranslation";
import { useMemo } from "react";

export interface BankOption {
	value: string;
	label: string;
}

/**
 * Helper function to get translated bank label
 * @param bank - Bank name from BankName type
 * @param t - Translation function
 * @returns Translated bank label
 */
export function getBankLabel(
	bank: BankName,
	t: (key: TranslationKeys) => string,
): string {
	return t(`fastBanks.${bank}` as TranslationKeys);
}

export function useBankOptions({
	includeAllOption = true,
	allOptionLabel = "wallet.withdrawalHistoryFilters.status.all",
}: {
	includeAllOption?: boolean;
	allOptionLabel?: TranslationKeys;
} = {}): BankOption[] {
	const { t } = useTranslation();

	return useMemo(() => {
		const bankOptionsList = ALL_BANKS.map((bank) => ({
			value: bank,
			label: t(`fastBanks.${bank}` as TranslationKeys),
		}));

		if (includeAllOption) {
			return [
				{
					value: "",
					label: t(allOptionLabel as TranslationKeys),
				},
				...bankOptionsList,
			];
		}

		return bankOptionsList;
	}, [t, includeAllOption, allOptionLabel]);
}
